import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

import { KEYS } from '@highlight-cp/constants';
import { useDebouncedCallback } from '@highlight-cp/hooks';

import { MAX_LENGTH_REACHED_ALLOWED_KEYS } from '../../constants';
import {
  applyMarkdownFormatting,
  convertHtmlToMarkdown,
  getIsValueEmpty,
  insertLineBreak,
  pastePlainText,
} from '../../utils';
import type { UseAtMentionsReturn } from '../useAtMentions';

interface UseEventHandlersProps {
  atMentions: UseAtMentionsReturn;
  debounceMs: number;
  enableLineBreaks?: boolean;
  maxLength: number;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  commitOnBlur?: boolean;
  onInput?: (value: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  spanRef: React.RefObject<HTMLSpanElement | null>;
  value: string;
}

/**
 * ### useEventHandlers
 *
 * @description
 * Hook for handling inline editable user interactions and sanitization.
 *
 * @param {UseEventHandlersProps} props - The props for the hook
 * @param {number} props.debounceMs - The debounce time in milliseconds
 * @param {boolean} props.enableLineBreaks - Whether to enable Shift+Enter or Ctrl+Enter (Cmd+Enter on Mac) line break functionality
 * @param {number} props.maxLength - The maximum length of the input
 * @param {Function} props.onChange - The callback function to handle the input change
 * @param {React.RefObject<HTMLSpanElement>} props.spanRef - The ref to the span element
 * @param {string} props.placeholder - The placeholder text to show when the input is empty
 * @param {string} props.value - The value of the input
 *
 * @returns {UseEventHandlersReturn} The return value of the hook
 */
const useEventHandlers = ({
  atMentions,
  debounceMs,
  enableLineBreaks,
  maxLength,
  onChange,
  commitOnBlur = false,
  onFocus,
  onBlur,
  onInput,
  spanRef,
  placeholder,
  value,
}: UseEventHandlersProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [rawCharacterCount, setRawCharacterCount] = useState(value?.length || 0);
  const [shouldFlashMaxLength, setShouldFlashMaxLength] = useState(false);
  const flashMaxLengthTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (flashMaxLengthTimeoutRef.current !== undefined) {
        clearTimeout(flashMaxLengthTimeoutRef.current);
      }
    };
  }, []);
  const isShowingPlaceholder = useMemo(
    () => placeholder && getIsValueEmpty(value) && rawCharacterCount === 0,
    [placeholder, rawCharacterCount, value],
  );

  const onChangeHandler = (rawValue: string) => {
    // Convert HTML to markdown (this may decode HTML entities like &lt; to <)
    const markdownContent = convertHtmlToMarkdown(rawValue);

    // IMPORTANT: everything that passes through onChange needs to be plain text
    // NO HTML tags allowed in final markdown
    const sanitizedContent = DOMPurify.sanitize(markdownContent, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });

    // Additional sanitization for dangerous text patterns that DOMPurify doesn't catch
    const additionalSanitized = sanitizedContent
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/vbscript:/gi, '') // Remove vbscript: protocol
      .replace(/data:/gi, ''); // Remove data: protocol

    onChange?.(additionalSanitized);
  };

  // Create debounced onChange callback with sanitization
  const debouncedOnChange = useDebouncedCallback(onChangeHandler, debounceMs);

  // Handle paste and input events and update the raw content state
  // IMPORTANT: DO NOT SEND RAW CONTENT TO THE BACKEND APIS.
  const update = () => {
    const UNSAFE_rawContent_DO_NOT_SEND_TO_BACKEND = spanRef.current?.innerHTML || '';
    setRawCharacterCount(UNSAFE_rawContent_DO_NOT_SEND_TO_BACKEND.length);
    if (commitOnBlur) {
      return;
    }
    debouncedOnChange(UNSAFE_rawContent_DO_NOT_SEND_TO_BACKEND);
  };

  // Handle key down events and prevent the user from typing past the max length prop
  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (!spanRef.current) return;

    // Let @-mentions handle the event first (if enabled)
    if (atMentions) {
      const handled = atMentions.handleKeyDown(event);
      if (handled) return;
    }

    // Handle Cmd+B for bold and Cmd+I for italic
    if ((event.metaKey || event.ctrlKey) && (event.key === 'b' || event.key === 'i')) {
      event.preventDefault();
      applyMarkdownFormatting(event.key);
      update();
      return;
    }

    // Handle Enter key behavior (like Slack)
    if (event.key === KEYS.ENTER) {
      // If Shift+Enter or Ctrl+Enter (Cmd+Enter on Mac) and line breaks are enabled, insert line break at cursor position
      if ((event.shiftKey || event.ctrlKey || event.metaKey) && enableLineBreaks) {
        event.preventDefault();

        const success = insertLineBreak(spanRef.current);

        if (success) {
          update();
        }
        return;
      }

      // If just Enter, unfocus the input
      event.preventDefault();
      spanRef.current.blur();
      return;
    }

    const currentContent = spanRef.current.textContent || '';

    // Allow Ctrl/Cmd combinations (copy, paste, select all, etc.)
    // except for 'b' and 'i' which we handle above for markdown formatting
    if ((event.ctrlKey || event.metaKey) && event.key !== KEYS.B && event.key !== KEYS.I) {
      return;
    }

    // If at max length and trying to add content (not delete/navigate)
    if (
      currentContent.length >= maxLength &&
      !MAX_LENGTH_REACHED_ALLOWED_KEYS.includes(
        event.key as (typeof MAX_LENGTH_REACHED_ALLOWED_KEYS)[number],
      )
    ) {
      event.preventDefault();
      setShouldFlashMaxLength(true);
      if (flashMaxLengthTimeoutRef.current !== undefined) {
        clearTimeout(flashMaxLengthTimeoutRef.current);
      }
      flashMaxLengthTimeoutRef.current = setTimeout(() => {
        setShouldFlashMaxLength(false);
        flashMaxLengthTimeoutRef.current = undefined;
      }, 1000);
    }
  };

  // Handle input events and update the raw content state
  // IMPORTANT: DO NOT SEND RAW CONTENT TO THE BACKEND APIS.
  const handleInput = () => {
    if (!spanRef.current) return;

    if (onInput) {
      const sanitizedContent = DOMPurify.sanitize(spanRef.current.textContent || '', {
        ALLOWED_TAGS: [], // Only plain text allowed - markdown is stored as text
        ALLOWED_ATTR: [],
      });
      onInput(sanitizedContent);
    }

    // Notify @-mentions about input changes
    atMentions?.handleInput();

    update();
  };

  const handleFocus = () => {
    // The span is empty while the placeholder is shown (rendered via CSS), so the
    // caret naturally lands at the start — no manual cursor positioning needed.
    setIsFocused(true);
    onFocus?.();
  };

  // Handle paste events and update the raw content state
  // IMPORTANT: DO NOT SEND RAW CONTENT TO THE BACKEND APIS.
  const handlePaste = (event: React.ClipboardEvent<HTMLSpanElement>) => {
    event.preventDefault();

    // Use pastePlainText with maxLength constraint
    pastePlainText(event, maxLength);

    update();
  };

  const handleBlur = () => {
    setIsFocused(false);

    // Close @-mention menu on blur
    atMentions?.closeMentionMenu();

    // If showing placeholder when blurring, send empty string instead of placeholder text
    if (isShowingPlaceholder) {
      onChangeHandler('');
    } else {
      onChangeHandler(spanRef.current?.innerHTML || '');
    }

    onBlur?.();
  };

  return {
    isShowingPlaceholder,
    handleInput,
    handleKeyDown,
    handleFocus,
    handlePaste,
    handleBlur,
    isFocused,
    rawCharacterCount,
    shouldFlashMaxLength,
  };
};

export default useEventHandlers;
